import {
  Activity,
  Archive,
  ArrowLeftRight,
  BadgeCheck,
  Boxes,
  Building2,
  CalendarCheck,
  ClipboardList,
  ClipboardCheck,
  Factory,
  FileStack,
  GitCompareArrows,
  Link2,
  ListChecks,
  Lock,
  Network,
  Package,
  PackagePlus,
  PieChart,
  ReceiptText,
  CircleDollarSign,
  ScanLine,
  Ship,
  Sparkles,
  TrendingDown,
  Truck,
  Trash2,
  Wallet,
  Warehouse,
  type LucideIcon,
  ShieldCheck,
} from "lucide-react";

/**
 * The Supply navigation model — one source of truth for the phone tab bar's
 * five slots AND the grouped desktop rail.
 *
 * `flagKey` is the runtime system flag each surface already self-enforces with
 * `isFlagEnabled(key, false) || notFound()`. The rail must gate on the SAME
 * key or it ships dead links: as of 2026-08-17 prod holds supply_hygiene_enabled,
 * supply_interco_enabled and supply_spend_enabled = true (all three flipped
 * live) — none of the three is dark, so treat that as a snapshot, not a
 * standing fact, and re-check `system_flags` before relying on it. Approvals
 * carries a null flagKey because its gate is a per-USER capability, resolved
 * pre-paint by the separate fb_supply_tier cookie — global config and
 * per-user capability are two mechanisms and stay two mechanisms.
 *
 * `capability` is the SAME per-user capability tier the surface's own
 * page.tsx checks as its first real guard (requireSupplyFloor /
 * requireSupplyApprover / requireSupplyItemMaster / requireOpeningStockEditor
 * — see lib/supply/session.ts). resolveEnabledSurfaces() intersects flag AND
 * capability so the rail never offers a door the session's own capabilities
 * cannot open — a chef_hq session (manage.write_all, NO outlet_ops.*) must
 * never see "Stock on hand" only to be bounced by requireSupplyFloor.
 * Approvals carries capability: null too — same reason as flagKey null, its
 * gate stays the separate fb_supply_tier mechanism, not this one.
 *
 * Group `hue` is the ADHD-colour anchor: the rail must never read as a grey
 * list. Values name supply theme tokens (--amber, --info), not raw colours.
 */
export type SupplyNavKey =
  | "order"
  | "orders"
  | "alibaba"
  | "deliveries"
  | "receive"
  | "count"
  | "transfer"
  | "waste"
  | "stor"
  | "ck"
  | "ck-transfer-prices"
  | "on-hand"
  | "opening-stock"
  | "food-cost"
  | "shrink"
  | "waste-counts"
  | "spend"
  | "match"
  | "checks"
  | "interco"
  | "close"
  | "close-transfers"
  | "close-stock"
  | "credits"
  | "approvals"
  | "hygiene"
  | "history"
  | "items"
  | "canvas"
  | "menu-coverage"
  | "packaging"
  | "plumbing"
  | "xero-map";

/** Five slots: four task tabs + More. */
export type SupplyTab = "order" | "receive" | "count" | "waste";

/**
 * The capability tier a surface's page.tsx gates on as its FIRST real guard
 * (lib/supply/session.ts). null means the surface's gate is not this
 * mechanism at all (approvals: the separate fb_supply_tier per-user cookie).
 *
 * Two tiers are not requireSupply* wrappers:
 *   finance_operational — Closing stock is the ops month-close sheet re-hosted,
 *     and keeps the ops page's own gate (requireFinanceOperational:
 *     finance.read_all | finance.read_operational); its Freeze buttons need it.
 *   transfers_group — the HQ all-outlets transfers desk (?group=1), whose page
 *     decides with canSeeTransfersGroup (app/supply/transfer/group-gate.ts).
 *   close_hub — the month-close hub reads every outlet unscoped, so it is the
 *     approver-view tier minus a scoped outlet_manager's manage.write
 *     (requireCloseHubView, app/supply/close/gate.ts).
 */
export type SupplyNavCapability =
  | "floor"
  | "approver"
  | "orders_view"
  | "item_master"
  | "opening_stock"
  | "xero_map"
  | "ck_transfer_price"
  | "finance_operational"
  | "transfers_group"
  | "close_hub"
  | null;

export type SupplyNavItem = {
  key: SupplyNavKey;
  href: string;
  label: string;
  icon: LucideIcon;
  /** Runtime system flag, or null when the surface gates on capability only. */
  flagKey: string | null;
  /** The page's real capability guard — see SupplyNavCapability above. */
  capability: SupplyNavCapability;
  /**
   * Set when this entry is a VIEW onto another entry's route — same page.tsx,
   * different query (Close's "Transfers · all outlets" is /supply/transfer
   * ?group=1). The host page's own gate still runs on that route, so the rail
   * offers the view only while the HOST's flag is on too (surfaces.ts), and
   * the nav drift guards check the host page against the HOST's flag and
   * capability — the view's own flagKey/capability gate the door, not the page.
   */
  viewOf?: SupplyNavKey;
};

export type SupplyNavGroup = {
  id: "floor" | "desk" | "manage";
  label: string;
  hue: "amber" | "info" | "success";
  items: SupplyNavItem[];
};

export const SUPPLY_NAV: SupplyNavGroup[] = [
  {
    id: "floor",
    label: "Floor",
    hue: "amber",
    items: [
      { key: "order", href: "/supply/order", label: "Order", icon: ClipboardList, flagKey: "supply_orders_enabled", capability: "floor" },
      { key: "receive", href: "/supply/receive", label: "Receive", icon: ScanLine, flagKey: "supply_grn_enabled", capability: "floor" },
      { key: "count", href: "/supply/count", label: "Stock count", icon: ListChecks, flagKey: "supply_count_enabled", capability: "floor" },
      { key: "transfer", href: "/supply/transfer", label: "Transfer", icon: ArrowLeftRight, flagKey: "supply_transfers_enabled", capability: "floor" },
      { key: "waste", href: "/supply/waste", label: "Waste", icon: Trash2, flagKey: "supply_waste_enabled", capability: "floor" },
      { key: "ck", href: "/supply/ck", label: "CK production", icon: Factory, flagKey: "supply_production_enabled", capability: "floor" },
      // MET's China-packaging room (MET_WH). Floor tier like the other floor
      // doors, but resolveEnabledSurfaces ALSO drops it for a session that
      // cannot open MET (surfaces.ts) — the page admits only MET's scope.
      { key: "stor", href: "/supply/stor", label: "China store", icon: Warehouse, flagKey: "supply_met_store_enabled", capability: "floor" },
    ],
  },
  {
    id: "desk",
    label: "Desk",
    hue: "info",
    items: [
      { key: "on-hand", href: "/supply/on-hand", label: "Stock on hand", icon: Boxes, flagKey: "supply_onhand_enabled", capability: "floor" },
      { key: "opening-stock", href: "/supply/opening-stock", label: "Opening stock", icon: PackagePlus, flagKey: "supply_opening_stock_enabled", capability: "opening_stock" },
      { key: "food-cost", href: "/supply/food-cost", label: "Food cost", icon: PieChart, flagKey: "supply_food_cost_enabled", capability: "floor" },
      { key: "shrink", href: "/supply/shrink", label: "Waste & shrink", icon: TrendingDown, flagKey: "supply_shrink_enabled", capability: "floor" },
      { key: "waste-counts", href: "/supply/waste-counts", label: "Waste & counts", icon: ClipboardCheck, flagKey: "supply_waste_counts_enabled", capability: "floor" },
      // Same flag as the floor's Order surface — one ordering lane, two doors,
      // and now ONE board. The floor raises and the desk clears, so monitoring
      // belongs to both; while this was approver-only the floor's only view of
      // its own orders was a rail stacked on top of the build screen.
      { key: "orders", href: "/supply/orders", label: "Orders", icon: FileStack, flagKey: "supply_orders_enabled", capability: "orders_view" },
      // Alibaba orders — the HQ desk for China imports (spec 2026-10-07). Close-hub tier, NOT approver:
      // the approver tier admits manage.write, which every scoped outlet manager holds (R8).
      { key: "alibaba", href: "/supply/alibaba", label: "Alibaba orders", icon: Ship, flagKey: "supply_alibaba_enabled", capability: "close_hub" },
      { key: "spend", href: "/supply/spend", label: "Spend & price creep", icon: Wallet, flagKey: "supply_spend_enabled", capability: "approver" },
      { key: "match", href: "/supply/match", label: "3-way match", icon: GitCompareArrows, flagKey: "supply_match_enabled", capability: "approver" },
      // Semakan Terimaan — HQ desk for receiving/billing problems across all outlets (spec 2026-09-29).
      { key: "checks", href: "/supply/checks", label: "Receiving checks", icon: ShieldCheck, flagKey: "supply_receiving_checks_enabled", capability: "close_hub" },
      // Month close (Finance + HQ). The hub is the Close module's landing. The
      // old "Interco monitor" entry is gone: it is the "Xero check" tab INSIDE
      // Inter-company now, and /supply/interco/monitor still renders it.
      { key: "close", href: "/supply/close", label: "Month close", icon: CalendarCheck, flagKey: "supply_close_enabled", capability: "close_hub" },
      { key: "interco", href: "/supply/interco", label: "Inter-company", icon: Building2, flagKey: "supply_interco_enabled", capability: "approver" },
      { key: "close-transfers", href: "/supply/transfer?group=1", label: "Transfers · all outlets", icon: ArrowLeftRight, flagKey: "supply_close_enabled", capability: "transfers_group", viewOf: "transfer" },
      { key: "close-stock", href: "/supply/close/stock", label: "Closing stock", icon: Lock, flagKey: "supply_close_enabled", capability: "finance_operational" },
      { key: "ck-transfer-prices", href: "/supply/ck/transfer-prices", label: "CK transfer prices", icon: CircleDollarSign, flagKey: "supply_production_enabled", capability: "ck_transfer_price" },
      { key: "deliveries", href: "/supply/deliveries", label: "Lalamove deliveries", icon: Truck, flagKey: "lalamove_booking_enabled", capability: "approver" },
      { key: "credits", href: "/supply/credits", label: "Supplier credits", icon: ReceiptText, flagKey: "supply_grn_enabled", capability: "approver" },
      { key: "approvals", href: "/supply/approvals", label: "Approvals", icon: BadgeCheck, flagKey: "supply_orders_enabled", capability: null },
      { key: "hygiene", href: "/supply/hygiene", label: "Hygiene queue", icon: Sparkles, flagKey: "supply_hygiene_enabled", capability: "approver" },
      { key: "menu-coverage", href: "/supply/menu-coverage", label: "Menu coverage", icon: Network, flagKey: "supply_menu_coverage_enabled", capability: "approver" },
      { key: "packaging", href: "/supply/packaging", label: "Packaging", icon: Package, flagKey: "supply_packaging_enabled", capability: "approver" },
      { key: "history", href: "/supply/history", label: "FMH history", icon: Archive, flagKey: "supply_fmh_history_enabled", capability: "approver" },
    ],
  },
  {
    id: "manage",
    label: "Manage",
    hue: "success",
    items: [
      { key: "items", href: "/supply/items", label: "Item master", icon: Boxes, flagKey: "supply_item_master_enabled", capability: "item_master" },
      { key: "canvas", href: "/supply/canvas", label: "Link canvas", icon: Network, flagKey: "supply_canvas_enabled", capability: "item_master" },
      { key: "plumbing", href: "/supply/plumbing", label: "Integration health", icon: Activity, flagKey: "supply_plumbing_enabled", capability: "approver" },
      { key: "xero-map", href: "/supply/xero-map", label: "Xero mapping", icon: Link2, flagKey: "supply_xero_map_enabled", capability: "xero_map" },
    ],
  },
];

/**
 * A1 module layer (proposal §2.1, DS V0.3 §08): the rail is FROZEN at these
 * seven modules — the original six plus Close (Shaq, 2026-09-25: one place
 * for Finance and HQ to close the month; Inter-company moved out of Kitchen). A new feature lands as a tab or drawer inside a module — never
 * a new rail entry. Items are the same objects as SUPPLY_NAV's; the module
 * layer is a VIEW over the one source of truth, so flag keys, cookie keys and
 * CSS gating classes stay exactly as they are.
 */
export type SupplyModuleId = "order" | "receive" | "stock" | "kitchen" | "insights" | "close" | "items";

export type SupplyModule = {
  id: SupplyModuleId;
  label: string;
  hue: "amber" | "info" | "success";
  icon: LucideIcon;
  items: [SupplyNavItem, ...SupplyNavItem[]];
};

const ITEM = (() => {
  const map = new Map(SUPPLY_NAV.flatMap((g) => g.items).map((i) => [i.key, i]));
  return (key: SupplyNavKey): SupplyNavItem => {
    const item = map.get(key);
    if (!item) throw new Error(`nav item missing for key ${key}`);
    return item;
  };
})();

export const SUPPLY_MODULES: SupplyModule[] = [
  { id: "order",    label: "Order",    hue: "amber",   icon: ClipboardList, items: [ITEM("order"), ITEM("approvals"), ITEM("orders"), ITEM("alibaba")] },
  { id: "receive",  label: "Receive",  hue: "info",    icon: ScanLine,      items: [ITEM("receive"), ITEM("checks"), ITEM("match"), ITEM("credits")] },
  { id: "stock",    label: "Stock",    hue: "success", icon: Boxes,         items: [ITEM("count"), ITEM("transfer"), ITEM("waste"), ITEM("on-hand"), ITEM("opening-stock"), ITEM("stor")] },
  { id: "kitchen",  label: "Kitchen",  hue: "amber",   icon: Factory,       items: [ITEM("ck"), ITEM("ck-transfer-prices"), ITEM("deliveries")] },
  { id: "insights", label: "Insights", hue: "info",    icon: PieChart,      items: [ITEM("food-cost"), ITEM("spend"), ITEM("shrink"), ITEM("waste-counts"), ITEM("history")] },
  { id: "close",    label: "Close",    hue: "amber",   icon: CalendarCheck, items: [ITEM("close"), ITEM("interco"), ITEM("close-transfers"), ITEM("close-stock")] },
  { id: "items",    label: "Items",    hue: "success", icon: Sparkles,      items: [ITEM("items"), ITEM("canvas"), ITEM("menu-coverage"), ITEM("packaging"), ITEM("hygiene"), ITEM("plumbing"), ITEM("xero-map")] },
];

const MODULE_OF: ReadonlyMap<SupplyNavKey, SupplyModuleId> = new Map(
  SUPPLY_MODULES.flatMap((m) => m.items.map((i) => [i.key, m.id] as const)),
);

export function moduleForKey(key: SupplyNavKey | null): SupplyModuleId | null {
  return key ? (MODULE_OF.get(key) ?? null) : null;
}

const ALL_KEYS: ReadonlySet<string> = new Set(
  SUPPLY_NAV.flatMap((g) => g.items.map((i) => i.key)),
);

export const GATED_NAV_KEYS: SupplyNavKey[] = SUPPLY_NAV.flatMap((g) => g.items)
  .filter((i) => i.flagKey !== null)
  .map((i) => i.key);

export const SUPPLY_NAV_COOKIE = "fb_supply_nav";
export const SUPPLY_NAV_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * Space-separated so CSS can test membership with [data-supply-nav~="key"],
 * then URL-encoded so the cookie value carries no space, semicolon or comma.
 */
export function encodeNavCookie(keys: SupplyNavKey[]): string {
  return encodeURIComponent(keys.join(" "));
}

/** Tolerant by design: an unknown key is dropped, never thrown on. */
export function decodeNavCookie(raw: string): SupplyNavKey[] {
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    decoded = raw;
  }
  return decoded
    .split(/\s+/)
    .filter((k): k is SupplyNavKey => ALL_KEYS.has(k));
}
